import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import html2pdf from "html2pdf.js";
import { Download, ArrowLeft } from "lucide-react";

const CoverPage = () => (
  <header className="report-cover tf-cover">
    <div className="cover-accent tf-accent"></div>
    <div className="cover-content">
      <h1 className="cover-title">
        Closing the<br />Verification Gap
      </h1>
      <div className="cover-subtitle">
        A Structural Solution to Pharmaceutical Fraud<br />
        Across Medicare and Medicaid
      </div>
      <div className="cover-companion">
        Policy Analysis for the Task Force to Eliminate Fraud
      </div>
      <div className="cover-date">March 2026</div>
      <div className="cover-classification">
        CONFIDENTIAL — FOR POLICY REVIEW
      </div>
    </div>
    <div className="page-number" style={{ color: '#94a3b8' }}>1</div>
  </header>
);

const ExecutiveSummary = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h2 className="main-section-title">EXECUTIVE SUMMARY</h2>
    <div className="stats-row">
      <div className="stat-box tf-stat"><span className="stat-number">$56.7B</span><span className="stat-label">Medicare improper payments FY2025</span></div>
      <div className="stat-box tf-stat"><span className="stat-number">$37.4B</span><span className="stat-label">Medicaid improper payments (6.12%)</span></div>
      <div className="stat-box tf-stat"><span className="stat-number">$6.8B</span><span className="stat-label">DOJ FCA recoveries FY2025 (record)</span></div>
      <div className="stat-box tf-stat"><span className="stat-number">324</span><span className="stat-label">Defendants in largest HCF takedown ever</span></div>
    </div>

    <div className="callout-box tf-structural">
      <strong>THE STRUCTURAL PROBLEM:</strong> The federal government has built the most sophisticated claims analytics engine in history. Congress mandated a pharmaceutical serialization infrastructure that generates unit-level verification data at every pharmacy in America. No one has connected them. The result: pharmacy claims are paid based on billing data alone, without verifying that the physical drug was actually dispensed.
    </div>

    <p className="body-text">The Executive Order explicitly calls for "pre-payment integrity and risk controls, including affirmative documentation requirements concerning services provided." To our knowledge, serialized reimbursement verification is the only technology class that delivers this capability for pharmaceutical claims.</p>

    <ul className="bullet-list">
      <li><strong>$94+ billion annually</strong> in combined Medicare and Medicaid improper payments. Current enforcement is reactive — claims are paid first, investigated later, recovered sometimes.</li>
      <li><strong>Five major federal fraud initiatives</strong> launched in the past 12 months. None verifies physical pharmaceutical dispensing. The gap is not in detection capability — it is in the verification layer.</li>
      <li><strong>The infrastructure already exists.</strong> Under DSCSA, 41B+ serialized products are tracked. By November 2026, every pharmacy generates unit-level data. The cost was borne by industry. It is sunk.</li>
      <li><strong>Serialized reimbursement verification</strong> — middleware connecting supply chain data to claims adjudication — converts pharmacy reimbursement from trust-based to verification-based. Phantom dispensing, diversion, and duplicate billing become materially more difficult to execute undetected.</li>
      <li><strong>Recommendation:</strong> The Task Force should endorse a multi-state pilot. No federal appropriation required. $0 cost to participating states. 90 days to generate the first empirical evidence of the pharmacy verification gap.</li>
    </ul>

    <div className="pull-quote tf-quote">
      "We are replacing the old 'pay and chase' model with a real-time 'detect and deploy' strategy, using advanced AI tools to identify fraud instantly and stop improper payments before they go out the door."
      <cite>— HHS Secretary Robert F. Kennedy Jr., February 25, 2026</cite>
    </div>

    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">2</div>
  </section>
);

const Section1 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h2 className="main-section-title">1 THE SCALE AND STRUCTURE OF THE PROBLEM</h2>

    <h3 className="subsection-title">1.1 The Current Fraud Landscape</h3>
    <p className="body-text">The magnitude of improper payments in federal healthcare programs has reached a scale that no enforcement apparatus — however aggressive — can recover after the fact.</p>

    <h4 className="case-subsection">Medicare (FY2025):</h4>
    <ul className="bullet-list">
      <li>Total improper payments: <strong>$56.73 billion</strong> (FFS: $28.83B; Part C: $23.67B; Part D: $4.23B)</li>
      <li>Part D improper payment rate: <strong>4.00%</strong> (up from 3.54% in FY2024)</li>
    </ul>

    <h4 className="case-subsection">Medicaid (FY2025):</h4>
    <ul className="bullet-list">
      <li>Total improper payments: <strong>$37.39 billion</strong> at a <strong>6.12%</strong> PERM rate</li>
      <li>National Medicaid spending reached <strong>$957.4 billion</strong> in FY2024</li>
    </ul>

    <h4 className="case-subsection">Enforcement Results (FY2025):</h4>
    <ul className="bullet-list">
      <li>DOJ False Claims Act recoveries: <strong>$6.8 billion</strong> — highest annual total in FCA history</li>
      <li>National healthcare fraud takedown (June 2025): <strong>324 defendants</strong> / <strong>$14.6 billion</strong> in alleged fraud</li>
      <li>State MFCUs: <strong>$2 billion</strong> recovered, returning <strong>$4.64</strong> for every <strong>$1</strong> invested</li>
      <li>CMS suspended <strong>$5.7 billion</strong> in suspected fraudulent Medicare payments</li>
      <li>CMS revoked billing privileges for <strong>5,586</strong> providers and suppliers</li>
    </ul>

    <div className="callout-box">
      <strong>The Minnesota Inflection Point:</strong> On February 25, 2026, CMS deferred <strong>$259.5 million</strong> from Minnesota — <strong>$243.8M</strong> for unsupported or potentially fraudulent claims. Federal prosecutors estimate fraud may exceed <strong>$9 billion</strong>. CMS has warned deferrals could exceed <strong>$1 billion</strong> per year. Minnesota is not an outlier. It is a preview.
    </div>

    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">3</div>
  </section>
);

const Section1_2 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>

    <h3 className="subsection-title">1.2 Why Recovery Is Structurally Insufficient</h3>
    <p className="body-text">The dominant model for addressing healthcare fraud is reactive: pay the claim, audit the pattern, investigate the anomaly, prosecute the offender, recover the funds. Its structural limitations are now quantifiable.</p>
    <ul className="bullet-list">
      <li><strong>Recovery rates are a fraction of losses.</strong> The DOJ's record <strong>$6.8 billion</strong> in FCA recoveries represents approximately <strong>7.2%</strong> of the <strong>$94 billion</strong> in identified improper payments. The remaining 92.8% is unrecovered.</li>
      <li><strong>Time-to-recovery is measured in years.</strong> The Kouza Brothers pharmacy fraud operated from 2010 to 2019 — <strong>nine years</strong> before prosecution. The Boyd Brothers HIV diversion scheme operated for years before sentencing in March 2026.</li>
      <li><strong>The asymmetry is structural.</strong> Fraudsters operate at the speed of claims submission — milliseconds. Investigators operate at the speed of litigation — years.</li>
      <li>The MFCU ROI illustrates the constraint. <strong>$4.64</strong> return per <strong>$1</strong> invested is impressive for recovery. A prevention model that stops the payment has theoretically unbounded ROI — the loss never materializes.</li>
    </ul>

    <h3 className="subsection-title">1.3 The Task Force Mandate</h3>
    <p className="body-text">On March 16, 2026, President Trump signed Executive Order "Establishing the Task Force to Eliminate Fraud," creating a cross-agency body chaired by Vice President JD Vance. On March 27, 2026, Vance convened the inaugural meeting at the White House with approximately half of the President's cabinet.</p>

    <div className="pull-quote tf-quote">
      "This is not just the theft of the American people's money. It is also the theft of critical services that the American people rely on."
      <cite>— Vice President JD Vance, March 27, 2026</cite>
    </div>

    <p className="body-text">Critically, the Executive Order explicitly calls for <strong>"pre-payment integrity and risk controls, including affirmative documentation requirements concerning services provided."</strong> This language aligns precisely with the infrastructure class described in this analysis.</p>

    <h3 className="subsection-title">1.4 The Five Federal Initiatives — What They Do and Don't</h3>
    <table className="data-table">
      <thead><tr><th>Initiative</th><th>What It Does</th><th>Serialization</th><th>Product Verification</th></tr></thead>
      <tbody>
        <tr><td>CMS CRUSH (Feb 2026)</td><td>RFI for comprehensive fraud regs; 5,586 provider revocations</td><td>—</td><td>—</td></tr>
        <tr><td>CMS FDOC (Mar 2025)</td><td>Real-time AI provider screening; 90% high-risk hospice removal</td><td>—</td><td>—</td></tr>
        <tr><td>CMS WISER (Jan 2026)</td><td>Prior authorization AI pilot in 6 states</td><td>—</td><td>—</td></tr>
        <tr><td>DOJ Fusion Center (Jun 2025)</td><td>Multi-agency AI data sharing for fraud prosecution</td><td>—</td><td>—</td></tr>
        <tr><td>Vance Task Force (Mar 2026)</td><td>Cross-agency coordination; pre-payment controls mandate</td><td>TBD</td><td>TBD</td></tr>
      </tbody>
    </table>

    <p className="body-text"><strong>Analysis:</strong> All five initiatives strengthen claims analytics, provider profiling, and enforcement coordination. None verifies whether a physical pharmaceutical product was actually dispensed before the claim is paid. The gap is not in detection capability — it is in the verification layer between the physical supply chain and the financial pipeline.</p>

    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">4</div>
  </section>
);

const Section2 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h2 className="main-section-title">2 THE VERIFICATION GAP</h2>

    <h3 className="subsection-title">2.1 Two Parallel Pipelines That Never Connect</h3>
    <p className="body-text">The American healthcare system operates two fully digitized, fully functional pipelines for pharmaceutical products. They have never been connected.</p>
    <p className="body-text"><strong>Pipeline 1: The Physical Supply Chain.</strong> Under the Drug Supply Chain Security Act (DSCSA), every prescription drug package carries a unique serial number, verified at each handoff from manufacturer to patient. Over <strong>41 billion</strong> serialized products are tracked. <strong>290,000+</strong> participants are connected through the Verification Router Service (VRS).</p>
    <p className="body-text"><strong>Pipeline 2: The Financial Pipeline.</strong> When a pharmacy dispenses a medication, it submits a claim through MMIS (Medicaid) or a Part D plan sponsor (Medicare). The claim is adjudicated against formulary, prior authorization, and utilization rules, and payment is authorized.</p>

    <div className="callout-box warning">
      <strong>THE GAP:</strong> CMS processes and pays pharmacy claims without ever querying the serialization data that proves the drug was actually dispensed. The claim says a drug was dispensed. The serialization system can verify whether it was. No one asks.
    </div>

    <p className="body-text">This is not a technology failure. It is an architectural omission. The two systems were built independently and no one has built the middleware to connect them. The United States invested over <strong>$15 billion</strong> in pharmaceutical serialization infrastructure and over <strong>$10 billion</strong> in claims processing infrastructure — and neither system checks the other before disbursing taxpayer funds.</p>

    <h3 className="subsection-title">2.2 How Fraud Exploits the Gap</h3>
    <ul className="bullet-list">
      <li><strong>Phantom Dispensing:</strong> Claims filed for drugs never dispensed. No physical event occurred. The claims system has no mechanism to ask: "Was a serialized drug actually dispensed?"</li>
      <li><strong>Drug Diversion:</strong> Legitimate drugs purchased through authorized channels, then resold to secondary markets. Serialization verification would flag the serial number as previously dispensed.</li>
      <li><strong>Duplicate Billing:</strong> Same physical unit billed to multiple payers independently. Serialization verification flags the serial number as already associated with a prior claim.</li>
      <li><strong>Prescription Mills:</strong> High-volume prescribing driven by kickbacks. Serialization adds physical verification to financial patterns, revealing volume anomalies at the product level.</li>
      <li><strong>Counterfeit Products:</strong> Fake drugs entering the supply chain. Invalid or duplicate serial numbers are intercepted at verification.</li>
      <li><strong>Cross-Payer Fraud:</strong> Same scheme billing Medicare, Medicaid, and commercial payers simultaneously. Serialization is payer-agnostic: one verification event protects all payers.</li>
    </ul>

    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">5</div>
  </section>
);

const Section2_3 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h3 className="subsection-title">2.3 Case Evidence</h3>

    <div className="case-card highlight">
      <h5>Kouza Brothers — $15M Phantom Dispensing (Michigan, 2010–2019)</h5>
      <p>Raad Kouza, a licensed pharmacist, and his brother Ramis operated pharmacies in Detroit, billing Medicare, Medicaid, and BCBS of Michigan for prescription medications they did not dispense. The fraud operated across three payer systems simultaneously for <strong>nine years</strong> before prosecution. Both sentenced November 2025.</p>
      <p className="case-insight">The verification gap: A single query — "Was a serialized product scanned out of inventory for this claim?" — would likely have flagged every phantom claim at submission. The nine-year detection lag is a direct consequence of reliance on post-hoc analytics rather than pre-payment verification.</p>
    </div>

    <div className="case-card highlight">
      <h5>Boyd Brothers — $92M HIV Drug Diversion (Maryland, sentenced March 2026)</h5>
      <p>Patrick and Charles Boyd, owners of Safe Chain Solutions, purchased HIV medications from patient "buyback" operations and resold them into the legitimate supply chain. Pharmacies received drugs in unsealed, potentially contaminated conditions. Combined <strong>38 years</strong> in federal prison.</p>
      <p className="case-insight">The verification gap: The diverted drugs had valid serial numbers but their transaction histories showed they had already been dispensed to patients. Serialized verification would have rejected new claims against those serial numbers at the pre-payment stage.</p>
    </div>

    <div className="case-card highlight">
      <h5>Arizona AHCCCS — $564M in False Claims (2025)</h5>
      <p>A single defendant operating 41 addiction clinics submitted $650 million in false claims to Arizona's Medicaid program for services never provided, receiving <strong>$564 million</strong>. Patients were recruited from homeless shelters; documentation was fabricated. While this involved behavioral health, the mechanism — billing for events that never occurred — is identical to phantom pharmacy dispensing.</p>
    </div>

    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">6</div>
  </section>
);

const Section3 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h2 className="main-section-title">3 THE INFRASTRUCTURE ALREADY EXISTS</h2>

    <h3 className="subsection-title">3.1 The Drug Supply Chain Security Act</h3>
    <p className="body-text">The DSCSA (Pub. L. 113-54, Title II), enacted November 27, 2013, mandates that every prescription drug package in the United States carry a unique product identifier and be tracked from manufacturer to dispensing. This is not a proposal. This is existing federal law:</p>

    <div className="stats-row">
      <div className="stat-box tf-stat"><span className="stat-number">41B+</span><span className="stat-label">Serialized products already tracked</span></div>
      <div className="stat-box tf-stat"><span className="stat-number">290K+</span><span className="stat-label">Authenticated supply chain participants</span></div>
      <div className="stat-box tf-stat"><span className="stat-number">Nov 2026</span><span className="stat-label">Full pharmacy compliance deadline</span></div>
    </div>

    <ul className="bullet-list">
      <li><strong>Manufacturer serialization:</strong> Complete. Every FDA-regulated prescription drug package carries a unique serial number.</li>
      <li><strong>Wholesale distribution tracking:</strong> Active. Transaction information accompanies each product transfer.</li>
      <li><strong>Large dispenser verification:</strong> Active since November 2024.</li>
      <li><strong>Small dispenser compliance:</strong> Deadline November 27, 2026. Tens of thousands of remaining pharmacies.</li>
    </ul>
    <p className="body-text">The cost of this infrastructure — estimated at over <strong>$15 billion</strong> industry-wide — was borne entirely by the private sector under federal mandate. It is a sunk cost.</p>

    <h3 className="subsection-title">3.2 What DSCSA Data Contains</h3>
    <ul className="bullet-list">
      <li>Unique serial number per saleable unit (NDC + serial + lot + expiration, GS1 GTIN-14 format)</li>
      <li>Manufacturer identity and production timestamp</li>
      <li>Transaction Information: drug identity, parties, date, quantities</li>
      <li>Transaction History: record of each prior transfer back to manufacturer</li>
      <li>Transaction Statement: attestation that product is not suspect or illegitimate</li>
    </ul>

    <h3 className="subsection-title">3.3 The Connection That Doesn't Exist</h3>
    <ul className="bullet-list">
      <li>No federal program queries DSCSA data before paying a pharmacy claim.</li>
      <li>No state Medicaid agency connects serialization records to claims adjudication.</li>
      <li>No Medicare Part D plan sponsor verifies physical dispensing against serialization data.</li>
      <li>The CRUSH RFI does not reference DSCSA data or pharmaceutical serialization.</li>
      <li>The Task Force Executive Order does not reference supply chain verification.</li>
    </ul>
    <p className="body-text">This is not because the technology doesn't exist. It is because no one has built the middleware to connect the two systems — until now.</p>

    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">7</div>
  </section>
);

const Section4 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h2 className="main-section-title">4 SERIALIZED REIMBURSEMENT INFRASTRUCTURE</h2>

    <h3 className="subsection-title">4.1 The Engineering Requirement</h3>
    <p className="body-text">The engineering challenge is precisely defined: computationally bind a physical unit of medication to a financial claim before payment is authorized. Three data elements are required:</p>
    <ul className="bullet-list">
      <li><strong>Serial number (supply chain):</strong> proves the drug exists, was manufactured by an authorized entity, distributed through legitimate channels</li>
      <li><strong>Prescription ID (pharmacy dispensing system):</strong> proves the drug was dispensed pursuant to a valid prescription</li>
      <li><strong>Prescriber credential (state licensing databases):</strong> proves the prescribing authority is valid and active</li>
    </ul>
    <p className="body-text">When assembled into a composite verification lock and confirmed before claim adjudication, payment becomes conditional on lock integrity.</p>

    <h3 className="subsection-title">4.2 The Six-Step Chain of Custody</h3>
    <ol className="numbered-list">
      <li><strong>Manufacturer Serial Number</strong> — verified at production</li>
      <li><strong>Wholesale Distribution</strong> — tracked through authorized channels</li>
      <li><strong>Pharmacy Receipt</strong> — confirmed via DSCSA verification</li>
      <li><strong>Dispensing Event</strong> — matched to valid prescription</li>
      <li><strong>Prescriber Credential</strong> — validated against state licensing</li>
      <li><strong>Claim Submission</strong> — verified against serialization record</li>
    </ol>

    <h3 className="subsection-title">4.3 Fraud Prevention — Typology by Typology</h3>
    <ul className="bullet-list">
      <li><strong>Phantom Dispensing</strong> fails at Step 4 — no physical dispensing event recorded, no verification lock assembled.</li>
      <li><strong>Drug Diversion</strong> fails at Step 2/3 — broken distribution chain or prior dispensing event on serial number.</li>
      <li><strong>Duplicate Billing</strong> fails at Step 6 — serial number already matched to a prior claim across any payer.</li>
      <li><strong>Prescription Mills</strong> detectable at Steps 4/5 — volume anomalies visible at physical product level.</li>
      <li><strong>Counterfeits</strong> fail at Step 1 — serial number invalid or duplicated in manufacturer database.</li>
      <li><strong>Cross-Payer Fraud</strong> fails at Step 6 — payer-agnostic verification catches multi-payer schemes.</li>
    </ul>

    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">8</div>
  </section>
);

const Section4_5 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h3 className="subsection-title">4.5 Proactive vs. Reactive: A Structural Comparison</h3>
    <table className="data-table">
      <thead><tr><th>Dimension</th><th>Reactive Model (Current)</th><th>Proactive Model (Serialized Verification)</th></tr></thead>
      <tbody>
        <tr><td>When fraud detected</td><td>After payment</td><td>Before payment</td></tr>
        <tr><td>Recovery mechanism</td><td>Litigation, FCA, MFCU</td><td>Prevention — payment never made</td></tr>
        <tr><td>Time to impact</td><td>Years</td><td>Milliseconds</td></tr>
        <tr><td>Cost to government</td><td>Investigation + prosecution + partial recovery</td><td>$0 — infrastructure built by private sector under DSCSA</td></tr>
        <tr><td>Coverage</td><td>Claims analytics flagging</td><td>Physical product verification</td></tr>
        <tr><td>Cross-payer</td><td>Separate investigations</td><td>Single verification event, all payers</td></tr>
        <tr><td>ROI model</td><td>$4.64 per $1 (MFCU)</td><td>Theoretically unbounded (prevented loss)</td></tr>
        <tr><td>Scalability</td><td>Linear — more fraud requires more investigators</td><td>Platform — scales with claims volume</td></tr>
        <tr><td>Deterrent effect</td><td>Prosecutorial (after the fact)</td><td>Structural (fraud cannot enter)</td></tr>
      </tbody>
    </table>

    <h3 className="subsection-title">4.6 Compliance Framework</h3>
    <ul className="bullet-list">
      <li><strong>HIPAA:</strong> Serialization data does not contain PHI. Verification at product level, not patient level.</li>
      <li><strong>DSCSA:</strong> Operates within existing DSCSA data framework — data pharmacies already generate.</li>
      <li><strong>21 CFR Part 11:</strong> Audit trail compliant with FDA electronic records and signature requirements.</li>
      <li><strong>FedRAMP:</strong> Architecture designed to meet FedRAMP High baseline for government deployment.</li>
      <li><strong>State Privacy:</strong> Queries are yes/no confirmations, not data transfers — minimal privacy exposure.</li>
    </ul>

    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">9</div>
  </section>
);

const Section5 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h2 className="main-section-title">5 IMPLEMENTATION — HOW THIS DEPLOYS</h2>

    <h3 className="subsection-title">5.1 Architecture</h3>
    <p className="body-text">Serialized reimbursement infrastructure is middleware. It sits between existing dispensing data feeds and existing claims adjudication workflows. It does not replace any component of the current system.</p>
    <ul className="bullet-list">
      <li><strong>Pharmacy side:</strong> Connects to existing pharmacy management systems that already generate DSCSA data. No new hardware. No workflow changes.</li>
      <li><strong>Payer side:</strong> Connects to existing MMIS and Part D platforms via standard HL7/NCPDP/API interfaces. Verification inserted as an additional claims edit.</li>
      <li><strong>Data flow:</strong> Binary query at adjudication: "Does this claim match a verified dispensing event with unbroken chain of custody?" Claims that fail are held for review.</li>
    </ul>
    <p className="body-text"><strong>What this is NOT:</strong> Not a rip-and-replace of MMIS. Not a multi-year implementation. Not a new data collection mandate on pharmacies. Not a centralized government database. Data remains in private-sector networks; verification is query-based.</p>

    <h3 className="subsection-title">5.2 Pilot Design</h3>
    <table className="data-table">
      <thead><tr><th>Parameter</th><th>Specification</th></tr></thead>
      <tbody>
        <tr><td>Duration</td><td>90 days</td></tr>
        <tr><td>Participating states</td><td>2-3 state Medicaid agencies</td></tr>
        <tr><td>Drug categories</td><td>1-2 per state: specialty drugs (HIV, oncology, HCV) and/or controlled substances (opioids, stimulants)</td></tr>
        <tr><td>Deliverables</td><td>First empirical measure of pharmacy claim verification rates; identification of unverified claims by typology; evidence base for scaling</td></tr>
        <tr><td>Cost to states</td><td>$0 — platform costs absorbed by solution provider</td></tr>
        <tr><td>Federal appropriation</td><td>None required</td></tr>
      </tbody>
    </table>

    <h3 className="subsection-title">5.3 Scaling Pathway</h3>
    <ul className="bullet-list">
      <li><strong>Phase 1 (Months 1-3):</strong> Pilot — 2-3 states, 1-2 drug categories per state. Empirical evidence generated.</li>
      <li><strong>Phase 2 (Months 4-12):</strong> State-wide deployment. Extend to all drug categories. Integrate with MCO contracts and D-SNP plans.</li>
      <li><strong>Phase 3 (Months 12-24):</strong> Multi-state network. Cross-state serial number verification. Network effects increase detection.</li>
      <li><strong>Phase 4 (Months 24-36):</strong> Federal integration. CMS adopts as recognized program integrity technology. Enhanced matching formalized.</li>
    </ul>

    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">10</div>
  </section>
);

const Section5_4 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h3 className="subsection-title">5.4 First-Mover Advantage for States</h3>
    <ul className="bullet-list">
      <li><strong>Shape federal policy.</strong> Pilot generates data that informs CMS rulemaking and Task Force recommendations.</li>
      <li><strong>Position for enhanced matching.</strong> Operational infrastructure qualifies for enhanced federal matching when CMS formalizes standards.</li>
      <li><strong>Audit-ready evidence.</strong> Serialization verification provides auditable proof of advanced fraud prevention — critical after the Minnesota precedent.</li>
      <li><strong>Fiscal defense.</strong> Every dollar prevented is a dollar preserved as federal Medicaid funding contracts.</li>
    </ul>

    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">11</div>
  </section>
);

const Section6 = () => (
  <section className="page-section page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h2 className="main-section-title">6 RECOMMENDATION</h2>

    <h3 className="subsection-title">6.1 The Recommendation</h3>
    <p className="body-text"><strong>The Task Force to Eliminate Fraud should endorse a multi-state pilot of serialized reimbursement verification for pharmaceutical claims across Medicare and Medicaid.</strong></p>
    <ul className="bullet-list">
      <li>Direct CMS to designate serialized reimbursement verification as a recognized program integrity technology eligible for enhanced federal matching</li>
      <li>Identify 2-3 state Medicaid agencies willing to participate in a 90-day pilot</li>
      <li>Coordinate with FDA (DSCSA enforcement) and CMS (claims adjudication) to facilitate the data connection that neither agency has built independently</li>
      <li>Include serialized verification as a response category in the CRUSH rulemaking process</li>
      <li>Establish metrics for pilot evaluation: verification rates, flagged claims, prevented payments</li>
    </ul>

    <h3 className="subsection-title">6.2 Why This, Why Now</h3>
    <ol className="numbered-list detailed">
      <li><strong>DSCSA compliance reaching critical mass.</strong> November 2026: every pharmacy generates unit-level data. Infrastructure cost ($15B+) is sunk.</li>
      <li><strong>Task Force has the mandate but not the mechanism.</strong> The EO calls for "pre-payment integrity and risk controls." To our knowledge, serialized verification is the only technology providing physical product verification at the pre-payment stage.</li>
      <li><strong>Federal enforcement escalating against states.</strong> Minnesota deferral ($259.5M, warnings of $1B/year) signals a new paradigm. States need proactive prevention infrastructure.</li>
      <li><strong>Recovery is structurally insufficient.</strong> $6.8B in FCA recoveries = 7.2% of identified improper payments. Prevention changes the equation.</li>
      <li><strong>No existing initiative addresses the gap.</strong> Five major federal initiatives. None verifies physical dispensing.</li>
      <li><strong>Pilot is zero-cost to government.</strong> Infrastructure exists. Platform is built. Private sector absorbs costs. The only requirement is the policy decision.</li>
    </ol>

    <h3 className="subsection-title">6.3 About the Solution Provider</h3>
    <p className="body-text">MENDRx is a serialized ERP platform purpose-built for pharmaceutical supply chain compliance and reimbursement verification. To our knowledge, no incumbent claims-integrity or DSCSA vendor has publicly demonstrated an operational middleware layer connecting DSCSA serialization data directly to pre-payment claims adjudication. The MENDRx platform is designed to fill this gap.</p>
    <p className="body-text"><strong>Leadership:</strong> The founding team brings direct operating experience in pharmaceutical distribution, having built and operated distribution businesses generating over <strong>$680 million</strong> in annual revenue. This is not a technology company proposing to learn the pharmaceutical supply chain. It is a pharmaceutical supply chain company that has built the technology.</p>
    <p className="body-text"><strong>Platform:</strong> Designed to meet FedRAMP High baseline security requirements. FIPS 140-2 encryption. SOC 2 Type II audit readiness. Pilot-ready for immediate deployment. Status: State policy briefings delivered to governors' offices in Massachusetts, Michigan, Minnesota, and Kansas.</p>

    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">12</div>
  </section>
);

const AppendixA = () => (
  <section className="page-section appendix page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h2 className="main-section-title">APPENDICES</h2>
    <h3 className="subsection-title">APPENDIX A: SOURCE VERIFICATION INDEX</h3>
    <p className="body-text small">All data points verified against primary federal sources as of March 30, 2026, except where explicitly labeled estimated or approximate.</p>
    <table className="data-table compact">
      <thead><tr><th>#</th><th>Claim</th><th>Primary Source</th><th>Date</th></tr></thead>
      <tbody>
        <tr><td>1</td><td>$56.73B Medicare improper payments</td><td>CMS/HHS FY2025 Agency Financial Report</td><td>FY2025</td></tr>
        <tr><td>2</td><td>$37.39B Medicaid / 6.12% PERM rate</td><td>CMS FY2025 Improper Payments Fact Sheet</td><td>FY2025</td></tr>
        <tr><td>3</td><td>$6.8B DOJ FCA recoveries</td><td>DOJ Press Release, Jan 16, 2026</td><td>Jan 2026</td></tr>
        <tr><td>4</td><td>324 defendants / $14.6B takedown</td><td>DOJ/HHS-OIG Press Release</td><td>Jun 2025</td></tr>
        <tr><td>5</td><td>$957.4B national Medicaid FY2024</td><td>MACPAC MACStats Exhibit 16</td><td>Feb 2026</td></tr>
        <tr><td>6</td><td>MFCU $2B / $4.64 ROI</td><td>HHS OIG MFCU Annual Report</td><td>Mar 2026</td></tr>
        <tr><td>7</td><td>CMS $5.7B suspended; 5,586 revocations</td><td>CMS Press Release + CPI Dashboard</td><td>Feb 2026</td></tr>
        <tr><td>8</td><td>Minnesota $259.5M deferral</td><td>CMS Press Release, Feb 25, 2026</td><td>Feb 2026</td></tr>
        <tr><td>9</td><td>Minnesota fraud may exceed $9B</td><td>U.S. Attorney, D. of Minnesota</td><td>Dec 2025</td></tr>
        <tr><td>10</td><td>Kouza Brothers $15M (MI)</td><td>DOJ Press Release, Nov 21, 2025</td><td>Nov 2025</td></tr>
        <tr><td>11</td><td>Boyd Brothers $92M diversion</td><td>DOJ Press Release; Mar 2026</td><td>Mar 2026</td></tr>
        <tr><td>12</td><td>Task Force EO, Mar 16, 2026</td><td>whitehouse.gov/presidential-actions</td><td>Mar 2026</td></tr>
        <tr><td>13</td><td>First meeting; Vance/Ferguson quotes</td><td>AP, CBS MN, Washington Times</td><td>Mar 2026</td></tr>
        <tr><td>14</td><td>CRUSH RFI</td><td>Federal Register Doc. 2026-03968</td><td>Feb 2026</td></tr>
        <tr><td>15</td><td>FDOC 90% hospice removal</td><td>Kim Brandt (CMS COO)</td><td>Mar 2026</td></tr>
        <tr><td>16</td><td>WISER: 6 states, Jan 2026</td><td>CMS Innovation Center</td><td>Jan 2026</td></tr>
        <tr><td>17</td><td>DSCSA enacted Nov 27, 2013</td><td>Pub. L. 113-54, Title II</td><td>Nov 2013</td></tr>
        <tr><td>18</td><td>41B products; 290K+ participants</td><td>TraceLink; HDA industry reporting</td><td>2025</td></tr>
        <tr><td>19</td><td>Small dispenser deadline Nov 2026</td><td>FDA Waivers and Exemptions</td><td>Current</td></tr>
        <tr><td>20</td><td>Arizona AHCCCS $564M</td><td>DOJ, District of Arizona</td><td>Jun 2025</td></tr>
        <tr><td>21</td><td>Arbor Homecare $100M (MA)</td><td>DOJ, District of Massachusetts</td><td>Jul 2024</td></tr>
        <tr><td>22</td><td>RFK Jr. "detect and deploy"</td><td>CMS Press Release, Feb 25, 2026</td><td>Feb 2026</td></tr>
        <tr><td>23</td><td>Dr. Oz "padlocking the jar"</td><td>CMS Press Release, Feb 25, 2026</td><td>Feb 2026</td></tr>
      </tbody>
    </table>

    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">13</div>
  </section>
);

const AppendixBCDE = () => (
  <section className="page-section appendix page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h3 className="subsection-title">APPENDIX B: FRAUD CASE EVIDENCE SUMMARY</h3>
    <table className="data-table compact">
      <thead><tr><th>Case</th><th>Fraud Type</th><th>Amount</th><th>Duration</th><th>Payers</th><th>Serialization Prevention</th></tr></thead>
      <tbody>
        <tr><td>Kouza Brothers (MI)</td><td>Phantom dispensing</td><td>$15M</td><td>2010–2019 (9 years)</td><td>Medicare, Medicaid, BCBS</td><td>Fails Step 4</td></tr>
        <tr><td>Boyd Brothers (MD)</td><td>Drug diversion</td><td>$92M</td><td>Multi-year</td><td>Medicare, Medicaid, commercial</td><td>Fails Step 2</td></tr>
        <tr><td>Arizona AHCCCS</td><td>Phantom billing</td><td>$564M</td><td>Multi-year</td><td>Medicaid</td><td>Fails Step 4</td></tr>
        <tr><td>Arbor Homecare (MA)</td><td>Phantom billing</td><td>$100M</td><td>2013–2017</td><td>Medicaid</td><td>Fails Step 4</td></tr>
        <tr><td>Gilead Sciences</td><td>Kickback-induced Rx settlement</td><td>$202M</td><td>Multi-year</td><td>Medicare, Medicaid, commercial</td><td>Detectable Step 5</td></tr>
      </tbody>
    </table>

    <h3 className="subsection-title">APPENDIX C: DSCSA COMPLIANCE TIMELINE</h3>
    <table className="data-table compact">
      <thead><tr><th>Milestone</th><th>Date</th><th>Status</th></tr></thead>
      <tbody>
        <tr><td>DSCSA enacted</td><td>November 27, 2013</td><td className="verified">Complete</td></tr>
        <tr><td>Manufacturer serialization</td><td>November 27, 2017</td><td className="verified">Complete</td></tr>
        <tr><td>Wholesale distributor verification</td><td>November 27, 2019</td><td className="verified">Complete</td></tr>
        <tr><td>Enhanced drug distribution security</td><td>November 27, 2023</td><td className="verified">Complete</td></tr>
        <tr><td>Large dispenser verification</td><td>November 27, 2024</td><td className="verified">Active</td></tr>
        <tr><td>Small dispenser compliance</td><td>November 27, 2026</td><td className="partial">Pending – 8 months</td></tr>
      </tbody>
    </table>

    <h3 className="subsection-title">APPENDIX D: TASK FORCE EO — KEY PROVISIONS</h3>
    <table className="data-table compact">
      <thead><tr><th>Provision</th><th>EO Language</th><th>Relevance</th></tr></thead>
      <tbody>
        <tr><td>Sec. 3(a)(ii)</td><td>"pre-payment integrity and risk controls, including affirmative documentation requirements"</td><td>Direct alignment. Serialized verification is a pre-payment control with affirmative documentation.</td></tr>
        <tr><td>Sec. 3(a)(iii)</td><td>"evaluate indicators of fraud and high-risk vulnerabilities"</td><td>Direct alignment. The verification gap is the highest-risk vulnerability in pharmacy claims.</td></tr>
        <tr><td>Sec. 3(a)(iv)</td><td>"promote information and data sharing and coordination"</td><td>Direct alignment. Connects DSCSA data (FDA/industry) with claims data (CMS/states).</td></tr>
        <tr><td>Sec. 3(a)(v)</td><td>"disrupt and dismantle fraud networks...repeat cross-program offenders"</td><td>Direct alignment. Cross-payer serial number verification identifies repeat offenders across all payers.</td></tr>
        <tr><td>Sec. 4(b)(ii)</td><td>"pre-payment integrity and risk controls" for anti-fraud requirements</td><td>Direct alignment. Directs agencies to adopt pre-payment controls — the exact technology class described.</td></tr>
      </tbody>
    </table>

    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">14</div>
  </section>
);

const AppendixEF = () => (
  <section className="page-section appendix page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <h3 className="subsection-title">APPENDIX E: FEDERAL INITIATIVE CAPABILITY MATRIX</h3>
    <table className="data-table compact">
      <thead><tr><th>Capability</th><th>CRUSH</th><th>FDOC</th><th>WISER</th><th>Fusion Center</th><th>Task Force</th><th>Serialized Verification</th></tr></thead>
      <tbody>
        <tr><td>Claims pattern analysis</td><td className="verified">&#10003;</td><td className="verified">&#10003;</td><td className="verified">&#10003;</td><td className="verified">&#10003;</td><td>Coord.</td><td>—</td></tr>
        <tr><td>Provider profiling</td><td className="verified">&#10003;</td><td className="verified">&#10003;</td><td className="verified">&#10003;</td><td className="verified">&#10003;</td><td>Coord.</td><td>—</td></tr>
        <tr><td>Prior authorization</td><td className="verified">&#10003;</td><td></td><td className="verified">&#10003;</td><td></td><td></td><td>—</td></tr>
        <tr><td>Post-hoc prosecution</td><td className="verified">&#10003;</td><td></td><td></td><td className="verified">&#10003;</td><td>Coord.</td><td>—</td></tr>
        <tr><td>Physical product verification</td><td></td><td></td><td></td><td></td><td></td><td className="verified">&#10003;</td></tr>
        <tr><td>Pre-payment prevention</td><td>Partial</td><td>Partial</td><td>Partial</td><td>Partial</td><td>Mandate</td><td className="verified">&#10003;</td></tr>
        <tr><td>Cross-payer detection</td><td></td><td></td><td></td><td></td><td>Mandate</td><td className="verified">&#10003;</td></tr>
        <tr><td>Serial number tracking</td><td></td><td></td><td></td><td></td><td></td><td className="verified">&#10003;</td></tr>
      </tbody>
    </table>

    <h3 className="subsection-title">APPENDIX F: COMPETITIVE LANDSCAPE</h3>
    <p className="body-text">To our knowledge, no incumbent claims-integrity or DSCSA vendor has publicly demonstrated an operational middleware layer connecting DSCSA serialization data directly to pre-payment claims adjudication.</p>
    <table className="data-table compact">
      <thead><tr><th>Provider</th><th>What They Do</th><th>Pre-Payment Verification</th><th>Serialization Integration</th></tr></thead>
      <tbody>
        <tr><td>Cotiviti</td><td>Claims analytics, payment accuracy</td><td>Post-payment</td><td>No</td></tr>
        <tr><td>Optum/Change HC</td><td>Claims processing, fraud analytics</td><td>Pattern detection</td><td>No</td></tr>
        <tr><td>SAS Institute</td><td>Fraud detection analytics</td><td>Pattern detection</td><td>No</td></tr>
        <tr><td>Gainwell Technologies</td><td>Medicaid claims processing (MMIS)</td><td>Claims edits</td><td>No</td></tr>
        <tr><td>IBM Watson Health</td><td>AI-driven claims analysis</td><td>Predictive analytics</td><td>No</td></tr>
        <tr><td>TraceLink</td><td>DSCSA compliance platform</td><td>—</td><td>Yes (supply chain only)</td></tr>
        <tr><td>rfxcel/Antares</td><td>DSCSA compliance platform</td><td>—</td><td>Yes (supply chain only)</td></tr>
        <tr className="highlight-row"><td>MENDRx</td><td>Serialized reimbursement infrastructure</td><td>Yes — pre-payment</td><td>Yes — connected to claims</td></tr>
      </tbody>
    </table>

    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">15</div>
  </section>
);

const ClosingSection = () => (
  <section className="page-section closing-section page-break-before">
    <div className="section-header-bar tf-header-bar">
      <span className="header-brand">POLICY ANALYSIS — TASK FORCE TO ELIMINATE FRAUD</span>
      <span className="header-conf">CONFIDENTIAL</span>
    </div>
    <p className="body-text small" style={{ textAlign: 'center', marginBottom: '1rem' }}>
      Document version: FINAL | March 30, 2026<br />
      Verification: Verified against primary federal sources except where explicitly labeled estimated, derived, or approximate.<br />
      Classification: CONFIDENTIAL — For Policy Review
    </p>
    <div className="contact-box">
      <p className="contact-header">For questions or to schedule a pilot discussion:</p>
      <div className="contact-info">
        <div className="contact-person">
          <strong>Steve Madsen</strong>, Co-Founder & CEO, MENDRx
        </div>
        <div className="contact-person">
          <strong>Tony Madsen</strong>, Co-Founder & COO, MENDRx
        </div>
      </div>
    </div>
    <div className="page-footer">
      <span>Independent Policy Analysis | March 2026</span>
      <span>Closing the Verification Gap</span>
    </div>
    <div className="page-number">16</div>
  </section>
);

export default function TaskForceBrief() {
  const reportRef = useRef(null);
  const navigate = useNavigate();
  const [isExporting, setIsExporting] = useState(false);

  const exportToPDF = async () => {
    setIsExporting(true);
    const opt = {
      margin: [0.5, 0.6, 0.7, 0.6],
      filename: 'Closing_the_Verification_Gap_Task_Force_Brief.pdf',
      image: { type: 'jpeg', quality: 0.98 },
      html2canvas: { scale: 2, useCORS: true },
      jsPDF: { unit: 'in', format: 'letter', orientation: 'portrait' },
      pagebreak: { mode: 'css', before: '.page-break-before' }
    };
    try {
      await html2pdf().set(opt).from(reportRef.current).save();
    } catch (error) {
      console.error('PDF export failed:', error);
    }
    setIsExporting(false);
  };

  return (
    <div className="report-container document-style">
      <div className="report-nav-bar">
        <motion.button
          className="back-button"
          onClick={() => navigate('/')}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="back-to-dashboard-btn"
        >
          <ArrowLeft size={18} />
          All Reports
        </motion.button>
        <motion.button
          className="export-button"
          onClick={exportToPDF}
          disabled={isExporting}
          whileHover={{ scale: 1.05 }}
          whileTap={{ scale: 0.95 }}
          data-testid="export-pdf-btn"
        >
          <Download size={20} />
          {isExporting ? 'Generating PDF...' : 'Export to PDF'}
        </motion.button>
      </div>

      <div className="report-wrapper document" ref={reportRef}>
        <CoverPage />
        <main className="report-main">
          <ExecutiveSummary />
          <Section1 />
          <Section1_2 />
          <Section2 />
          <Section2_3 />
          <Section3 />
          <Section4 />
          <Section4_5 />
          <Section5 />
          <Section5_4 />
          <Section6 />
          <AppendixA />
          <AppendixBCDE />
          <AppendixEF />
          <ClosingSection />
        </main>
      </div>
    </div>
  );
}
